import React from 'react';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-dark-500 py-6 px-4 max-w-4xl mx-auto">
      <header className="text-center mb-6">
        <h1 className="text-3xl font-pixel text-primary-400 mb-2">HOW NOSTRIE WORKS</h1>
        <p className="text-sm text-accent-400 font-mono">Understanding the Nostr Network Pet</p>
        <p className="mt-2">
          <a href="/" className="text-primary-400 hover:text-primary-300 underline font-mono">← Back to Main Page</a>
        </p>
      </header>
      
      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">How Metrics Are Calculated</h2>
        <div className="bg-dark-800 p-4 rounded font-mono text-sm space-y-6">
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Hunger (Note Activity)</h3>
            <p className="text-gray-300 mb-2">Hunger represents the level of note creation activity on the Nostr network.</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-300">
              <li>Increases when note creation rate is above the baseline</li>
              <li>Decreases when note creation rate falls below the baseline</li>
              <li>Has a natural tendency to gravitate toward 70%</li>
              <li>Maximum change rate: 5% per update cycle</li>
              <li>The baseline adapts over time using 24-hour averages</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Energy (Zap Values)</h3>
            <p className="text-gray-300 mb-2">Energy represents the level of zap activity on the Nostr network.</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-300">
              <li>Increases when zap activity is above the baseline</li>
              <li>Decreases when zap activity falls below the baseline</li>
              <li>Has a natural tendency to gravitate toward 70%</li>
              <li>More volatile than hunger with a maximum change rate of 10% per update cycle</li>
              <li>The baseline adapts over time using 24-hour averages</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Happiness (Moscow Time)</h3>
            <p className="text-gray-300 mb-2">Happiness is based on Moscow Time (sats/USD), which represents Bitcoin's value in satoshis per US dollar.</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-300">
              <li>Increases when current Moscow Time is higher than the 24-hour average</li>
              <li>Decreases when current Moscow Time is lower than the 24-hour average</li>
              <li>Has a natural tendency to gravitate toward 70%</li>
              <li>Highly volatile with a maximum change rate of 15% per update cycle</li>
              <li>Changes are proportional to the ratio between current value and baseline</li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Calculation Details</h3>
            <p className="text-gray-300 mb-2">All three metrics use a similar formula:</p>
            <ul className="list-disc pl-5 space-y-1 text-gray-300">
              <li>Activity ratios are calculated as current rate ÷ baseline</li>
              <li>Higher ratios lead to increases, lower ratios lead to decreases</li>
              <li>A hyperbolic tangent function is used to moderate extreme values</li>
              <li>The system balances the natural pull toward 70% with the influence of activity ratio</li>
              <li>Updates occur every 5 minutes with gradual changes</li>
            </ul>
          </div>
        </div>
      </div>
      
      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">How States Work</h2>
        <div className="bg-dark-800 p-4 rounded font-mono text-sm space-y-6">
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Positive States</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-success-400 font-bold">EXCELLENT</p>
                <p className="text-gray-300">Average of all stats above 80%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is thriving! The Nostr network is very healthy.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-success-300 font-bold">GOOD</p>
                <p className="text-gray-300">Average of all stats above 60%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is feeling good. The network is in good shape.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-warning-300 font-bold">FAIR</p>
                <p className="text-gray-300">Average of all stats above 40%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is doing okay. Network activity is moderate.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-warning-400 font-bold">POOR</p>
                <p className="text-gray-300">Average of all stats below 40%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie isn't feeling great. The network could use more activity.</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Warning States</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-warning-500 font-bold">HUNGRY</p>
                <p className="text-gray-300">Hunger is lowest stat and below 60%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is getting hungry. Network activity is below normal.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-warning-500 font-bold">TIRED</p>
                <p className="text-gray-300">Energy is lowest stat and below 60%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is tired. Zap energy is lower than usual.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-warning-500 font-bold">SAD</p>
                <p className="text-gray-300">Happiness is lowest stat and below 60%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is feeling sad. Moscow Time is declining.</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Critical States</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-danger-500 font-bold">STARVING</p>
                <p className="text-gray-300">Hunger is lowest stat and below 30%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is starving! The network needs more notes.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-danger-500 font-bold">EXHAUSTED</p>
                <p className="text-gray-300">Energy is lowest stat and below 30%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is exhausted! Zap activity is very low.</p>
              </div>
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-danger-500 font-bold">DEPRESSED</p>
                <p className="text-gray-300">Happiness is lowest stat and below 30%</p>
                <p className="text-gray-400 text-xs mt-1">Nostrie is depressed. Moscow Time is falling.</p>
              </div>
            </div>
          </div>
          
          <div>
            <h3 className="text-accent-400 mb-2 text-lg">Special States</h3>
            <div className="grid grid-cols-1 gap-4">
              <div className="bg-dark-700 p-3 rounded">
                <p className="text-danger-500 font-bold">DISCONNECTED</p>
                <p className="text-gray-300">No connection to Nostr relays</p>
                <p className="text-gray-400 text-xs mt-1">Unable to connect to the Nostr network. Please check your connection.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="arcade-container mb-6">
        <h2 className="text-xl font-pixel mb-4 text-primary-400">State Transitions</h2>
        <div className="bg-dark-800 p-4 rounded font-mono text-sm">
          <p className="text-gray-300 mb-4">Nostrie's state is determined by evaluating all metrics in the following order:</p>
          
          <ol className="list-decimal pl-5 space-y-2 text-gray-300">
            <li>First, check for critical conditions (starving, exhausted, depressed)</li>
            <li>If no critical conditions, check for warning conditions (hungry, tired, sad)</li>
            <li>If no warning conditions, determine overall state (excellent, good, fair, poor)</li>
            <li>State transitions occur immediately based on the evaluation of all metrics</li>
          </ol>
          
          <p className="text-gray-300 mt-4">The overall health percentage shown is the average of all three metrics (hunger, energy, happiness).</p>
        </div>
      </div>
      
      <footer className="text-center text-gray-500 text-xs font-mono mt-8 mb-4">
        <p>Nostrie monitors the health of the Nostr network in real-time.</p>
        <p className="mt-2">
          © {new Date().getFullYear()} Nostrie Project
          <span className="mx-2">|</span>
          <a href="/raw-data-log" className="text-primary-400 hover:text-primary-300">Log</a>
        </p>
      </footer>
    </div>
  );
};

export default HowItWorks; 